import { Tabs as ArkTabs } from "@ark-ui/react";
import { ReactNode, useCallback } from "react";

import {
  ControlButtonGroup,
  ControlElement,
  ControlToggle,
} from "@/features/shared/widgets/map/map.styles";

import styles from "./map-control-tabs.module.css";

export type MapControlPanel = "config" | "legend" | null;

export interface MapControlTabsRootProps {
  value: MapControlPanel;
  onValueChange: (value: MapControlPanel) => void;
  children: ReactNode;
}

function Root({ value, onValueChange, children }: MapControlTabsRootProps) {
  const arkValue = value ?? "";
  const handleChange = useCallback(
    (details: { value: string }) => {
      const next = details.value || null;
      onValueChange(next as MapControlPanel);
    },
    [onValueChange],
  );
  return (
    <ArkTabs.Root value={arkValue} onValueChange={handleChange} deselectable>
      {children}
    </ArkTabs.Root>
  );
}

export interface MapControlTabsListProps {
  children: ReactNode;
}

function List({ children }: MapControlTabsListProps) {
  return (
    <ControlButtonGroup>
      <ArkTabs.List className={styles.list}>{children}</ArkTabs.List>
    </ControlButtonGroup>
  );
}

export interface MapControlTabsTriggerProps {
  value: string;
  "aria-label": string;
  children: ReactNode;
}

function Trigger({ value, "aria-label": ariaLabel, children }: MapControlTabsTriggerProps) {
  return (
    <ArkTabs.Context>
      {(context: { value: string | null }) => {
        const isActive = context.value === value;
        return (
          <ArkTabs.Trigger value={value} aria-label={ariaLabel} asChild>
            <ControlToggle
              variant="primary"
              open={isActive}
              active={isActive}
              aria-expanded={isActive}
            >
              {children}
            </ControlToggle>
          </ArkTabs.Trigger>
        );
      }}
    </ArkTabs.Context>
  );
}

export interface MapControlTabsContentProps {
  value: string;
  children: ReactNode;
}

function Content({ value, children }: MapControlTabsContentProps) {
  return (
    <ArkTabs.Content value={value}>
      <ControlElement>{children}</ControlElement>
    </ArkTabs.Content>
  );
}

export const MapControlTabs = {
  Root,
  List,
  Trigger,
  Content,
};

export default MapControlTabs;
